/**
 * What an experiment's run length was planned on, as stored in `experiments.plan`.
 *
 * Shared by the update endpoint, which rejects anything else, and the dashboard, which
 * reads it back. It is a json column written from a request body, so it is parsed on
 * read too rather than trusted: a row written by hand or by an older client must
 * degrade to "no plan", not to NaN in the UI.
 *
 * Traffic has three sources, and at most one of `dailySessions` / `siteSharePct` is set:
 *
 *   both null       the typical traffic of past experiments on this website
 *   siteSharePct    this share of the website's own sessions (Pertento's session
 *                   tracking), stored as the share so the plan follows the live traffic
 *   dailySessions   a figure the planner typed
 */
export type PlanAssumptions = {
  /** Relative lift worth detecting, in percent: 5 is +5%. */
  liftPct: number;
  /** Sessions per day entering the test, typed by the planner, or null. */
  dailySessions: number | null;
  /** Share of the website's sessions that reach the test, in percent, or null. */
  siteSharePct: number | null;
  /** The original's conversion rate in percent, or null to use the estimate. */
  ratePct: number | null;
};

/** Beyond this a "lift" is a different page, and the maths stops meaning anything. */
const MAX_LIFT_PCT = 500;

const isPositive = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

/** The assumptions, or null for anything that is not a complete, sane set. */
export function parsePlanAssumptions(raw: unknown): PlanAssumptions | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { liftPct, dailySessions, ratePct } = raw as Record<string, unknown>;
  // Absent on plans saved before the site-traffic option existed: those used history.
  const siteSharePct = (raw as Record<string, unknown>).siteSharePct ?? null;

  if (!isPositive(liftPct) || liftPct > MAX_LIFT_PCT) return null;
  if (dailySessions !== null && !isPositive(dailySessions)) return null;
  if (siteSharePct !== null && (!isPositive(siteSharePct) || siteSharePct > 100)) return null;
  if (dailySessions !== null && siteSharePct !== null) return null;
  if (ratePct !== null && (!isPositive(ratePct) || ratePct >= 100)) return null;

  return {
    liftPct,
    dailySessions: dailySessions === null ? null : Math.round(dailySessions),
    siteSharePct,
    ratePct,
  };
}
